import {FormEvent, useRef, useState} from "react";
import {API_URL, KKTU_OPTIONS, LIMIT_FILE_SIZE, LIMIT_FILE_SIZE_MB} from "../constants/constants";
import {ICreativeContext, IDtoOrdCreative, IKktuOption} from "../types/types";
import {toast} from "react-toastify";
import {authFetch} from "../services/api";
import Select, {SingleValue} from 'react-select';


interface CreativeFormProps {
    context: ICreativeContext;
}

export const CreativeForm = ({context}: CreativeFormProps) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [uploadedMedia, setUploadedMedia] = useState<{id: string; filename: string} | null>(null);
    const [lastErid, setLastErid] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState<IDtoOrdCreative>({
        name: "",
        brand: context.brand,
        category: "",
        contract_external_ids: [context.contract_external_id],
        contract_external_name: context.contract_external_name,
        media_external_ids: [],
        media_external_name: "",
        kktus: [],
        targeting: "Специалисты здравоохранения (врачи)",
        form: "",
        texts: [],
        medpointMaterialUrl: ""
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));
    };

    const handleTypeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value;
        setFormData(prev => ({...prev, form: newType, texts: []}));
        setUploadedMedia(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleKktuSelect = (selectedOption: SingleValue<IKktuOption>) => {
        const kktuValue = selectedOption?.value;
        setFormData(prev => ({...prev, kktus: kktuValue ? [kktuValue] : []}));
    };

    const handleFileChange = () => {
        setUploadedMedia(null);
    };

    const handleCopy = async () => {
        if (!lastErid) return;
        try {
            await navigator.clipboard.writeText(lastErid);
            toast.success('ERID скопирован');
        } catch {
            toast.error('Не удалось скопировать. Скопируйте вручную.');
        }
    };

    const needsMedia = formData.form === 'video' || formData.form === 'banner_html5';

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError("");

        const file = fileInputRef.current?.files?.[0];
        if (needsMedia && !uploadedMedia && !file) {
            setError("Пожалуйста, выберите медиа-файл");
            return;
        }
        if (file && file.size > LIMIT_FILE_SIZE) {
            setError(`Файл больше ${LIMIT_FILE_SIZE_MB} МБ. Выберите файл меньшего размера.`);
            return;
        }

        setLoading(true);

        try {
            let mediaIds: string[] = [];
            let mediaName = '';
            let media = uploadedMedia;
            if (needsMedia) {
                if (!media) {
                    const fd = new FormData();
                    fd.append("file", file!);
                    const mediaRes = await authFetch(`${API_URL}/ord/media`, {method: "PUT", body: fd});
                    const mediaData = await mediaRes.json();
                    if (!mediaRes.ok) throw new Error(mediaData.message || "Ошибка загрузки медиа");
                    media = {id: mediaData.media_external_id, filename: mediaData.filename};
                    setUploadedMedia(media);
                }
                mediaIds = [media.id];
                mediaName = media.filename;
            }

            const creativeBody = {
                ...formData,
                brand: context.brand,
                contract_external_ids: [context.contract_external_id],
                contract_external_name: context.contract_external_name,
                media_external_ids: mediaIds,
                media_external_name: mediaName,
            };
            const creativeRes = await authFetch(`${API_URL}/ord/creatives`, {
                method: "PUT",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify(creativeBody),
            });
            const creativeData = await creativeRes.json();
            if (!creativeRes.ok) throw new Error(creativeData.message || "Ошибка создания креатива");

            setLastErid(creativeData.erid);
            setUploadedMedia(null);
            setFormData(prev => ({
                ...prev,
                name: "",
                category: "",
                kktus: [],
                form: "",
                texts: [],
                medpointMaterialUrl: "",
                media_external_ids: [],
                media_external_name: "",
            }));
            if (fileInputRef.current) fileInputRef.current.value = "";
            toast.success('ERID получен');
        } catch (err) {
            console.error('Ошибка при отправке формы:', err);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container">
            <div className="form-wrapper">
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <p><strong>Токен маркировки:</strong> появится после сохранения креатива</p>

                        <label htmlFor="name" className="form-label">Название *</label>
                        <input
                            id="name"
                            name="name"
                            className="form-input"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Введите название"
                            required
                        />

                        <label htmlFor="brand" className="form-label">Юр.лицо *</label>
                        <input
                            id="brand"
                            name="brand"
                            className="form-input"
                            value={context.brand}
                            disabled
                        />

                        <label htmlFor="category" className="form-label">Название препарата *</label>
                        <input
                            id="category"
                            name="category"
                            className="form-input"
                            value={formData.category}
                            onChange={handleChange}
                            placeholder="Введите название"
                            required
                        />

                        <label htmlFor="contract" className="form-label">Договор *</label>
                        <input
                            id="contract"
                            name="contract"
                            className="form-input"
                            value={context.contract_external_name}
                            disabled
                        />

                        <label htmlFor="kktus" className="form-label">Код ККТУ *</label>
                        <Select
                            id="kktus"
                            name="kktus"
                            className="basic-single-select"
                            classNamePrefix="select"
                            placeholder="Выберите код ККТУ"
                            options={KKTU_OPTIONS}
                            value={KKTU_OPTIONS.find(option => option.value === formData.kktus[0])}
                            onChange={handleKktuSelect}
                            isSearchable
                            required
                            noOptionsMessage={() => "Коды ККТУ не найдены"}
                        />

                        <label htmlFor="targeting" className="form-label">Целевая аудитория *</label>
                        <input
                            id="targeting"
                            name="targeting"
                            className="form-input"
                            value={formData.targeting}
                            onChange={handleChange}
                            disabled
                            required
                        />

                        <label htmlFor="formType" className="form-label">Тип креатива *</label>
                        <select
                            id="formType"
                            name="formType"
                            className="form-input"
                            value={formData.form}
                            onChange={handleTypeSelect}
                            required
                            title="Выберите тип креатива"
                        >
                            <option value="">Выберите тип креатива</option>
                            <option value="text_block">Статья</option>
                            <option value="video">Видео</option>
                            <option value="banner_html5">Лендинг</option>
                        </select>

                        {formData.form === 'banner_html5' && (
                            <div className="helper-text">
                                Файл Лендинга и сопутствующие данные можно получить у разработчика.
                            </div>
                        )}

                        {needsMedia && (
                            <>
                                <label htmlFor="mediaFile" className="form-label">Медиафайл *</label>
                                <input
                                    type="file"
                                    id="mediaFile"
                                    ref={fileInputRef}
                                    className="form-input"
                                    accept="image/*,video/*,.doc,.docx,.pdf,.txt,.zip,.html"
                                    onChange={handleFileChange}
                                    disabled={loading}
                                    required={!uploadedMedia}
                                />
                                <div className="helper-text">
                                    Максимальный размер — {LIMIT_FILE_SIZE_MB} МБ.
                                </div>
                                {uploadedMedia && (
                                    <div className="helper-text">
                                        Медиа загружено: {uploadedMedia.filename}. Повторный сабмит использует этот файл.
                                    </div>
                                )}
                            </>
                        )}

                        {formData.form === 'text_block' && (
                            <>
                                <label htmlFor="textContent" className="form-label">Текст *</label>
                                <textarea
                                    id="textContent"
                                    name="texts"
                                    className="form-input"
                                    value={formData.texts[0] || ''}
                                    onChange={(e) => setFormData(prev => ({...prev, texts: [e.target.value]}))}
                                    placeholder="Введите текст"
                                    required
                                    rows={5}
                                />
                            </>
                        )}

                        <label htmlFor="medpointMaterialUrl" className="form-label">Ссылка на материал medpoint.pro *</label>
                        <input
                            id="medpointMaterialUrl"
                            name="medpointMaterialUrl"
                            className="form-input"
                            value={formData.medpointMaterialUrl}
                            onChange={handleChange}
                            placeholder="https://medpoint.pro/article/..."
                            required
                        />
                        {formData.medpointMaterialUrl && (() => {
                            const match = /(\d+)$/.exec(formData.medpointMaterialUrl.replace(/\/$/, ''));
                            return match ? <p className="form-hint">ID материала: {match[1]}</p> : null;
                        })()}

                        {error && <div className="error-message">{error}</div>}
                    </div>

                    <button
                        type="submit"
                        className={`button ${loading ? 'loading' : ''}`}
                        disabled={loading}
                    >
                        {!loading && 'Отправить'}
                    </button>
                </form>

                {lastErid && (
                    <div className="erid-block" style={{marginTop: 'var(--space-20)', padding: 'var(--space-12)', border: '1px solid #ccc', borderRadius: '8px'}}>
                        <p><strong>Последний полученный ERID:</strong></p>
                        <input
                            type="text"
                            readOnly
                            value={lastErid}
                            className="form-input"
                            onFocus={(e) => e.target.select()}
                        />
                        <button
                            type="button"
                            className="button"
                            style={{marginTop: 'var(--space-8)'}}
                            onClick={handleCopy}
                        >
                            Скопировать
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
